import { createGlobalStyle } from 'styled-components';
import { colors, colorTokens } from '@ardoq/design-tokens';

const PRESENTATION_CHROME_SCOPE_CLASS = 'presentation-chrome';
const DARK_MODE_CLASS = 'dark-mode';

/**
 * Applied to the presentation chrome so it picks up the design system's
 * dark-mode semantic colour tokens while the slide content keeps rendering
 * against the light palette it was authored for.
 */
export const PRESENTATION_CHROME_CLASS = `${PRESENTATION_CHROME_SCOPE_CLASS} ${DARK_MODE_CLASS}`;

/**
 * Marks a portalled panel as one of the viewer's own controls, so the slide
 * keyboard shortcuts stay out of its way, while leaving it on the light
 * palette. For panels hosting content that only has light-mode styling.
 */
export const PRESENTATION_LIGHT_CHROME_CLASS = PRESENTATION_CHROME_SCOPE_CLASS;

export const PRESENTATION_CHROME_SELECTOR = `.${PRESENTATION_CHROME_SCOPE_CLASS}`;

/**
 * These dark-mode token values in @ardoq/design-tokens do not hold the polarity
 * their names imply, which the chrome is the first surface to depend on:
 *
 * - `textSubtle` resolves to grey200, one step *brighter* than `textDefault`
 *   (grey300), so subtitles and captions render as near-white.
 * - `surfaceActionGhost` is an opaque grey950 rather than transparent, and
 *   `surfaceActionGhostHover` is grey900 — the value the chrome uses for its own
 *   `surfaceDefaultSubtle` surfaces. Ghost buttons therefore read as dark chips
 *   at rest and dissolve into the surface on hover. Replaced with translucent
 *   white overlays, mirroring the black overlays light mode uses.
 * - `surfaceActionGhostDisabled` resolves to grey400 while the enabled state is
 *   grey950, so a disabled ghost button becomes a light filled block — more
 *   prominent than its enabled siblings. Both are transparent in light mode.
 * - `iconActionHover` has no dark-mode value, so it stays grey900: the same
 *   colour as the surface, making hovered, pressed and active icons invisible.
 * - `iconActionDisabled` has no dark-mode value at all, so it stays grey300.
 * - `surfaceHover` resolves to the same grey900 as `surfaceDefaultSubtle`,
 *   making hovered rows on a subtle surface (e.g. the slide overview list)
 *   indistinguishable from their resting state.
 *
 * The `.dark-mode` class only redeclares the colour variables, and
 * `@ardoq/typography/base.css` sets `color: var(--text-default)` on `body`, so
 * descendants inherit the light-mode colour as a computed value. The scope
 * redeclares `color` to resolve the token again in dark mode.
 */
export const PresentationChromeGlobalStyle = createGlobalStyle`
  .${PRESENTATION_CHROME_SCOPE_CLASS}.${DARK_MODE_CLASS} {
    color: ${colors.textDefault};

    --text-subtle: var(--icon);
    --surface-action-ghost: transparent;
    --surface-action-ghost-hover: ${colorTokens.white100};
    --surface-action-ghost-pressed: ${colorTokens.white200};
    --surface-action-ghost-disabled: transparent;
    --icon-action-hover: var(--icon-strong);
    --icon-action-disabled: var(--text-disabled);
    --surface-hover: ${colorTokens.grey800};
  }
`;
